import { type AxiosError } from 'axios'
import { AXIOS_ERROR_CODES, humanFileSize } from '@deepcloud/deep-ui-utils'
import { ERROR_CODES } from '../error-codes.ts'
import { TASK_UPLOAD_STATUS, TaskUpload, TaskUploadStatus } from '../types'

type StatusInfo = {
  icon: string
  color: string
  title: string
}

export const TASK_UPLOAD_STATE_MAP: Record<TaskUploadStatus, StatusInfo> = {
  [TASK_UPLOAD_STATUS.ADDED]: {
    icon: '',
    color: '',
    title: 'upload.status.added',
  },
  [TASK_UPLOAD_STATUS.COMPLETED]: {
    icon: 'far fa-circle-check',
    color: 'success',
    title: 'upload.status.completed',
  },
  [TASK_UPLOAD_STATUS.PROCESSING]: {
    icon: 'far fa-spinner',
    color: 'blue',
    title: 'upload.status.processing',
  },
  [TASK_UPLOAD_STATUS.ERROR]: {
    icon: 'far fa-circle-xmark',
    color: 'error',
    title: 'upload.status.error',
  },
  [TASK_UPLOAD_STATUS.WARNING]: {
    icon: 'far fa-circle-exclamation',
    color: 'warning',
    title: 'upload.status.warning',
  },
  [TASK_UPLOAD_STATUS.CANCELED]: {
    icon: 'far fa-ban',
    color: '',
    title: 'upload.status.canceled',
  },
}

function getTaskUploadIconNode(taskUpload: TaskUpload) {
  if (!taskUpload) return undefined
  const foldersCount = taskUpload.folders
    ? Object.keys(taskUpload.folders).length
    : 0
  const hasMultipleFolders = foldersCount > 0

  const filesCount = taskUpload.files ? taskUpload.files?.length : 0
  const hasMultipleFiles = filesCount > 1
  if (hasMultipleFolders || hasMultipleFiles) return undefined
  if (filesCount === 1) {
    if (taskUpload.files && taskUpload.files[0].status === 'completed') {
      return taskUpload.files[0].data?.node
    }
  }
  return undefined
}

function getTaskUploadTotalSize(taskUpload: TaskUpload) {
  let totalSize = 0
  taskUpload.files?.forEach((f) => {
    if (f.data?.file) {
      totalSize += f.data?.file.size
    }
  })

  return totalSize
}

function getTaskUploadTotalSizeDisplay(taskUpload: TaskUpload) {
  return humanFileSize(getTaskUploadTotalSize(taskUpload))
}

function getTaskUploadItemsFoldersCount(taskUpload: TaskUpload) {
  return taskUpload.folders ? Object.keys(taskUpload.folders).length : 0
}

function getTaskUploadItemsFilesCount(taskUpload: TaskUpload) {
  return taskUpload.files ? taskUpload.files?.length : 0
}

function taskUploadHasFolders(taskUpload: TaskUpload) {
  return taskUpload.folders && Object.keys(taskUpload.folders).length > 0
}

function taskUploadHasFiles(taskUpload: TaskUpload) {
  return taskUpload.files && taskUpload.files.length > 0
}

export function useTaskUpload() {
  const { t } = useI18n()

  function getTaskUploadStateIcon(state: TaskUploadStatus) {
    const statusInfo = TASK_UPLOAD_STATE_MAP[state]

    if (!statusInfo) {
      return {
        icon: '',
        color: '',
        title: t('Unknown'), // translate fallback string as well
      }
    }

    return {
      ...statusInfo,
      title: t(statusInfo.title),
    }
  }

  function getTaskUploadErrorMsgFormatted(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: string | AxiosError | Error | any,
  ) {
    if (!error) return undefined

    let message: string

    if (typeof error === 'string') {
      message = error
    } else if (error instanceof Error) {
      message = error.message
    } else if (error?.message) {
      message = error.message
    } else {
      message = String(error)
    }

    if (message === ERROR_CODES.KC_ERROR_UPDATE_TOKEN) {
      return t('error.codes.KC_ERROR_UPDATE_TOKEN')
    } else if (message === ERROR_CODES.ERR_FILE_NOT_FOUND) {
      return t('error.codes.ERR_FILE_NOT_FOUND')
    } else if (message === ERROR_CODES.UPLOAD_PARENT_NODE_ID_CAN_NOT_BE_EMPTY) {
      return t('error.codes.UPLOAD_PARENT_NODE_ID_CAN_NOT_BE_EMPTY')
    }

    const errorRes = error?.response
    const errorCode = error?.code
    const errorStatusCode = errorRes?.status
    const errorResData = errorRes?.data

    if (errorStatusCode === 413) {
      return t('error.413.file_too_big')
    } else if (errorCode === AXIOS_ERROR_CODES.ERR_NETWORK) {
      return t('error.axios.ERR_NETWORK')
    } else if (errorCode === AXIOS_ERROR_CODES.ERR_CANCELED) {
      return t('upload.error.axios.ERR_CANCELED')
    }

    const errorResDataMsg = errorResData?.message
    if (errorResDataMsg) return String(errorResDataMsg)

    // always return a string
    return String(message)
  }

  function onTaskUploadItemSetStatus(
    newStatus: TaskUploadStatus,
    taskUploadItem: TaskUpload,
  ) {
    const currentStatus = taskUploadItem.status

    // Never mutate if in a terminal state
    if (['completed', 'warning'].includes(currentStatus)) return

    // Rule: newStatus = 'added' → only allowed from canceled or error
    if (
      newStatus === 'added' &&
      !['canceled', 'error'].includes(currentStatus)
    ) {
      return
    }

    // Rule: newStatus = 'error' → only allowed from added
    if (newStatus === 'error' && currentStatus !== 'added') {
      return
    }

    taskUploadItem.status = newStatus
  }

  return {
    getTaskUploadIconNode,
    getTaskUploadStateIcon,
    getTaskUploadTotalSizeDisplay,
    getTaskUploadErrorMsgFormatted,
    onTaskUploadItemSetStatus,
    getTaskUploadItemsFoldersCount,
    getTaskUploadItemsFilesCount,
    taskUploadHasFolders,
    taskUploadHasFiles,
  }
}
